import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { FiUser, FiMail, FiLock, FiPhone, FiLogOut, FiEdit2, FiCheck, FiX, FiMapPin, FiShoppingBag, FiHeart, FiPackage, FiClock, FiCheckCircle, FiXCircle, FiTruck } from "react-icons/fi";
import { Link } from "react-router-dom";
import { apiGetMyOrders, apiTrackOrders, apiCancelOrder } from "../services/api";
import "./Account.css";

const WHATSAPP = "918096554464";

const ORDER_STAGES = [
  { key: "pending",   label: "Request Arrived",     icon: <FiClock /> },
  { key: "confirmed", label: "Payment Verified",    icon: <FiCheckCircle /> },
  { key: "shipped",   label: "Shipped",              icon: <FiTruck /> },
  { key: "delivered", label: "Delivered",            icon: <FiPackage /> },
];

const STAGE_INDEX = { pending: 0, confirmed: 1, shipped: 2, delivered: 3, cancelled: -1 };

const PROVINCES = ["Punjab", "Sindh", "KPK", "Balochistan", "Islamabad (ICT)", "AJK", "Gilgit-Baltistan"];

export default function Account() {
  const { user, login, register, logout, updateProfile, refreshProfile } = useAuth();
  const [tab, setTab]       = useState("login");
  const [section, setSection] = useState("profile"); // profile | address | orders
  const [orders, setOrders]   = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [cancellingId, setCancellingId]   = useState(null);
  const [cancelError, setCancelError]     = useState("");
  const [form, setForm]     = useState({ name: "", email: "", password: "", phone: "" });
  const [profile, setProfile] = useState({});
  const [editing, setEditing] = useState(false);
  const [error, setError]   = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      refreshProfile();
      setProfile({
        name: user.name || "", phone: user.phone || "",
        address: user.address || "", city: user.city || "",
        province: user.province || "", postal_code: user.postal_code || "",
      });
    }
  }, [user?.id]);

  const fetchOrders = () => {
    if (section !== "orders") return;
    setOrdersLoading(true);
    const byId = apiGetMyOrders().then(d => Array.isArray(d) ? d : []).catch(() => []);
    const byPhone = user?.phone
      ? apiTrackOrders(user.phone).then(d => Array.isArray(d) ? d : []).catch(() => [])
      : Promise.resolve([]);
    Promise.all([byId, byPhone]).then(([a, b]) => {
      const unique = Array.from(new Map([...a, ...b].map(o => [o.id, o])).values())
        .sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
      setOrders(unique);
      setOrdersLoading(false);
    });
  };

  useEffect(() => {
    fetchOrders();
  }, [section, user?.id]);

  useEffect(() => {
    window.addEventListener("focus", fetchOrders);
    return () => window.removeEventListener("focus", fetchOrders);
  }, [section, user?.id]);

  const handle    = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handlePro = (e) => setProfile(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleLogin = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(form.email, form.password); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await register(form.name, form.email, form.password, form.phone); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    setCancellingId(orderId);
    setCancelError("");
    const res = await apiCancelOrder(orderId, user.phone);
    if (res.error) { setCancelError(res.error); }
    else { setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: "cancelled" } : o)); }
    setCancellingId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setError(""); setSuccess(""); setLoading(true);
    try {
      await updateProfile(profile);
      setSuccess("Profile updated successfully!");
      setEditing(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // ── Logged-in view ──
  if (user) return (
    <div className="account-page">
      <div className="account-layout">

        {/* Sidebar */}
        <aside className="account-sidebar">
          <div className="account-avatar-lg">{user.name?.[0]?.toUpperCase() || "U"}</div>
          <h3>{user.name}</h3>
          <p className="sidebar-email">{user.email}</p>
          <div className="account-role-badge">{user.role === "admin" ? "👑 Admin" : "👤 Customer"}</div>
          <nav className="sidebar-nav">
            <button className={section === "profile" ? "active" : ""} onClick={() => setSection("profile")}><FiUser /> Personal Info</button>
            <button className={section === "address" ? "active" : ""} onClick={() => setSection("address")}><FiMapPin /> Shipping Address</button>
            <button className={section === "orders"  ? "active" : ""} onClick={() => setSection("orders")}><FiPackage /> My Orders</button>
            <Link to="/wishlist"><FiHeart /> My Wishlist</Link>
            <Link to="/shop"><FiShoppingBag /> Browse Shop</Link>
          </nav>
          <button className="btn-logout" onClick={logout}><FiLogOut /> Logout</button>
        </aside>

        {/* Main content */}
        <div className="account-main">
          {success && <div className="profile-success"><FiCheck /> {success}</div>}
          {error   && <div className="profile-error"><FiX /> {error}</div>}

          <form onSubmit={handleSave}>
            {section === "profile" && (
              <div className="profile-section">
                <div className="section-title-row">
                  <h2>Personal Information</h2>
                  {!editing
                    ? <button type="button" className="btn-edit" onClick={() => setEditing(true)}><FiEdit2 /> Edit</button>
                    : <div className="edit-actions">
                        <button type="submit" className="btn-save" disabled={loading}><FiCheck /> {loading ? "Saving..." : "Save"}</button>
                        <button type="button" className="btn-cancel-edit" onClick={() => setEditing(false)}><FiX /></button>
                      </div>
                  }
                </div>
                <div className="profile-grid">
                  <div className="profile-field">
                    <label><FiUser /> Full Name</label>
                    {editing
                      ? <input name="name" value={profile.name} onChange={handlePro} required />
                      : <span>{profile.name || "—"}</span>}
                  </div>
                  <div className="profile-field">
                    <label><FiMail /> Email</label>
                    <span className="field-locked">{user.email}</span>
                  </div>
                  <div className="profile-field">
                    <label><FiPhone /> Phone Number</label>
                    {editing
                      ? <input name="phone" value={profile.phone} onChange={handlePro} placeholder="03XX-XXXXXXX" />
                      : <span>{profile.phone || "—"}</span>}
                  </div>
                </div>
              </div>
            )}

            {section === "address" && (
              <div className="profile-section">
                <div className="section-title-row">
                  <h2>Shipping Address</h2>
                  {!editing
                    ? <button type="button" className="btn-edit" onClick={() => setEditing(true)}><FiEdit2 /> Edit</button>
                    : <div className="edit-actions">
                        <button type="submit" className="btn-save" disabled={loading}><FiCheck /> {loading ? "Saving..." : "Save"}</button>
                        <button type="button" className="btn-cancel-edit" onClick={() => setEditing(false)}><FiX /></button>
                      </div>
                  }
                </div>
                <div className="profile-grid">
                  <div className="profile-field full">
                    <label><FiMapPin /> Street Address</label>
                    {editing
                      ? <input name="address" value={profile.address} onChange={handlePro} placeholder="House #, Street, Area" />
                      : <span>{profile.address || "—"}</span>}
                  </div>
                  <div className="profile-field">
                    <label>City</label>
                    {editing
                      ? <input name="city" value={profile.city} onChange={handlePro} placeholder="e.g. Lahore" />
                      : <span>{profile.city || "—"}</span>}
                  </div>
                  <div className="profile-field">
                    <label>Province</label>
                    {editing
                      ? <select name="province" value={profile.province} onChange={handlePro}>
                          <option value="">Select province</option>
                          {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      : <span>{profile.province || "—"}</span>}
                  </div>
                  <div className="profile-field">
                    <label>Postal Code</label>
                    {editing
                      ? <input name="postal_code" value={profile.postal_code} onChange={handlePro} placeholder="e.g. 54000" />
                      : <span>{profile.postal_code || "—"}</span>}
                  </div>
                </div>
                {!editing && !profile.address && (
                  <p className="address-hint">💡 Add your shipping address to speed up checkout.</p>
                )}
              </div>
            )}
          </form>

          {/* ── ORDERS SECTION ── */}
          {section === "orders" && (
            <div className="profile-section">
              <div className="section-title-row">
                <h2>My Orders</h2>
                <span className="orders-count" style={{display:"flex",alignItems:"center",gap:"8px"}}>
                  {orders.length} order{orders.length !== 1 ? "s" : ""}
                  <button type="button" onClick={fetchOrders} style={{fontSize:"12px",padding:"2px 8px",cursor:"pointer"}}>↻ Refresh</button>
                </span>
              </div>
              {cancelError && <div className="profile-error"><FiX /> {cancelError}</div>}
              {ordersLoading ? (
                <p className="orders-loading">Loading orders...</p>
              ) : orders.length === 0 ? (
                <div className="orders-empty">
                  <FiPackage size={40} />
                  <p>No orders found.</p>
                  <Link to="/shop" className="btn-shop-now">Start Shopping</Link>
                </div>
              ) : (
                <div className="orders-list">
                  {orders.map(order => {
                    const stageIdx = STAGE_INDEX[order.status] ?? 0;
                    const isCancelled = order.status === "cancelled";
                    const canCancel = ["pending", "confirmed"].includes(order.status);
                    const canRefund = order.payment_status === "approved" && isCancelled;
                    const refundMsg = encodeURIComponent(
                      `Hi RizzGlam, I'd like to request a refund for Order #${order.id}.\n` +
                      `Amount: Rs. ${Number(order.total).toLocaleString()}\n` +
                      `Phone: ${order.customer_phone}\n` +
                      `Please process my refund. Thank you.`
                    );
                    return (
                      <div key={order.id} className={`order-track-card ${isCancelled ? "cancelled" : ""}`}>
                        <div className="otc-header">
                          <div>
                            <span className="otc-id">Order #{order.id}</span>
                            <span className="otc-date">{new Date(order.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>
                          </div>
                          <div className="otc-right">
                            <span className="otc-total">Rs. {Number(order.total).toLocaleString()}</span>
                            <span className={`otc-pay-badge pay-${order.payment_status}`}>
                              {order.payment_status === "pending_verification" ? "⏳ Awaiting Verification"
                                : order.payment_status === "approved" ? "✅ Payment Approved"
                                : "❌ Payment Rejected"}
                            </span>
                          </div>
                        </div>

                        {/* Items */}
                        <div className="otc-items">
                          {(Array.isArray(order.items) ? order.items : []).map((item, i) => (
                            <div key={i} className="otc-item">
                              <img src={item.image} alt={item.name} onError={e => e.target.src = "https://via.placeholder.com/40"} />
                              <div>
                                <span>{item.name}</span>
                                <small>{item.size && `Size: ${item.size}`}{item.color && ` · ${item.color}`} · Qty: {item.qty}</small>
                              </div>
                              <span>Rs. {((item.price || 0) * (item.qty || 1)).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>

                        {/* Progress bar */}
                        {isCancelled ? (
                          <div className="otc-cancelled-msg">
                            <FiXCircle /> Order Cancelled
                            {order.admin_note && <span> — {order.admin_note}</span>}
                          </div>
                        ) : (
                          <div className="otc-progress">
                            {ORDER_STAGES.map((stage, i) => (
                              <div key={stage.key} className={`progress-step ${
                                i < stageIdx ? "done" : i === stageIdx ? "active" : ""
                              }`}>
                                <div className="ps-connector" />
                                <div className="ps-circle">{i < stageIdx ? <FiCheck /> : stage.icon}</div>
                                <span className="ps-label">{stage.label}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Address */}
                        <div className="otc-address">
                          <FiMapPin size={13} /> {order.address_line}, {order.city}{order.postal_code ? " - " + order.postal_code : ""}
                        </div>

                        {/* Cancel & Refund actions */}
                        <div className="otc-actions">
                          {canCancel && (
                            <button
                              className="btn-cancel-order"
                              onClick={() => handleCancelOrder(order.id)}
                              disabled={cancellingId === order.id}
                            >
                              <FiXCircle /> {cancellingId === order.id ? "Cancelling..." : "Cancel Order"}
                            </button>
                          )}
                          {canRefund && (
                            <a
                              href={`https://wa.me/${WHATSAPP}?text=${refundMsg}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-refund"
                            >
                              <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="wa" width={15} />
                              Request Refund
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ── Auth view ──
  return (
    <div className="account-page">
      <div className="auth-card">
        <div className="auth-logo"><span className="lm">Rizz</span><span className="la">Glam</span></div>
        <div className="auth-tabs">
          <button className={tab === "login" ? "active" : ""} onClick={() => { setTab("login"); setError(""); }}>Login</button>
          <button className={tab === "register" ? "active" : ""} onClick={() => { setTab("register"); setError(""); }}>Register</button>
        </div>

        {tab === "login" ? (
          <form onSubmit={handleLogin} className="auth-form">
            <div className="auth-field"><FiMail /><input name="email" type="email" placeholder="Email address" value={form.email} onChange={handle} required /></div>
            <div className="auth-field"><FiLock /><input name="password" type="password" placeholder="Password" value={form.password} onChange={handle} required /></div>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="btn-auth" disabled={loading}>{loading ? "Logging in..." : "Login"}</button>
            <p className="auth-switch">Don't have an account? <button type="button" onClick={() => { setTab("register"); setError(""); }}>Register</button></p>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="auth-form">
            <div className="auth-field"><FiUser /><input name="name" type="text" placeholder="Full name" value={form.name} onChange={handle} required /></div>
            <div className="auth-field"><FiMail /><input name="email" type="email" placeholder="Email address" value={form.email} onChange={handle} required /></div>
            <div className="auth-field"><FiPhone /><input name="phone" type="tel" placeholder="Phone number (optional)" value={form.phone} onChange={handle} /></div>
            <div className="auth-field"><FiLock /><input name="password" type="password" placeholder="Password" value={form.password} onChange={handle} required /></div>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="btn-auth" disabled={loading}>{loading ? "Creating account..." : "Create Account"}</button>
            <p className="auth-switch">Already have an account? <button type="button" onClick={() => { setTab("login"); setError(""); }}>Login</button></p>
          </form>
        )}
      </div>
    </div>
  );
}
